// src/utils/storageUtils.js - Memory-based storage for artifacts compatibility
import { USER_TIERS } from '../services/UserService';
import featureAccess from './featureAccess';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';

// In-memory storage for artifacts (localStorage not supported)
const inMemoryStorage = {
  savedWorkouts: [],
  profiles: [],
  history: [],
  favorites: []
};

// Check if we can use the AuthContext to get the current user
let currentUserGetter = null;
let userServiceRef = null;

/**
 * Initialize the storage utils with dependencies
 * Call this from a component that has access to auth context
 * @param {Function} getCurrentUser - Function to get the current user
 * @param {Object} userService - User service reference
 */
export const initializeStorageUtils = (getCurrentUser, userService) => {
  currentUserGetter = getCurrentUser;
  userServiceRef = userService;
};

/**
 * Get current user if available
 * @returns {Object|null} Current user or null
 */
const getCurrentUser = () => {
  if (currentUserGetter) {
    return currentUserGetter();
  }
  return null;
};

/**
 * Check if we're in an artifact environment (no localStorage)
 */
const isArtifactEnvironment = () => {
  try {
    return typeof localStorage === 'undefined' || !localStorage;
  } catch (e) {
    return true;
  }
};

/**
 * Safe storage getter
 */
const getFromStorage = (key, fallback = []) => {
  if (isArtifactEnvironment()) {
    return inMemoryStorage[key] || fallback;
  }
  
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch (error) {
    console.warn('localStorage not available, using memory storage');
    return inMemoryStorage[key] || fallback;
  }
};

/**
 * Safe storage setter
 */
const setToStorage = (key, value) => {
  if (isArtifactEnvironment()) {
    inMemoryStorage[key] = value;
    return;
  }
  
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('localStorage not available, using memory storage');
    inMemoryStorage[key] = value;
  }
};

/**
 * Load saved workouts based on user state
 * @returns {Promise<Array>} Saved workouts
 */
export const loadSavedWorkouts = async () => {
  const user = getCurrentUser();
  
  // If logged in with Firebase, get workouts from Firestore
  if (user && user.uid) {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data().savedWorkouts || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to load saved workouts from Firestore:', error);
      return [];
    }
  }
  
  // Fall back to safe storage for guests
  return getFromStorage('savedWorkouts', []);
};

/**
 * Save a workout
 * @param {Object} workout - Workout to save
 * @param {Array} savedWorkouts - Current saved workouts
 * @returns {Promise<Array>} Updated saved workouts array
 */
export const saveWorkout = async (workout, savedWorkouts) => {
  if (!workout) return savedWorkouts;
  
  const user = getCurrentUser();
  const userTier = user?.uid 
    ? await getUserTierFromFirestore(user.uid) 
    : USER_TIERS.NONE;
  
  // Check if user has access to save workouts
  const access = featureAccess.checkAccess('savedWorkouts', savedWorkouts.length, userTier);
  if (!access.hasAccess || access.limitReached) {
    console.error('Cannot save workout: User has reached the limit or does not have access');
    return savedWorkouts;
  }
  
  // Create workout object with ID and date
  const workoutToSave = {
    ...workout,
    id: Date.now(),
    date: new Date().toISOString(),
  };
  
  // If user is logged in, save to Firestore
  if (user && user.uid) {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const savedWorkouts = userData.savedWorkouts || [];
        const updatedWorkouts = [...savedWorkouts, workoutToSave];
        
        await updateDoc(userDocRef, {
          savedWorkouts: updatedWorkouts
        });
        
        return updatedWorkouts;
      }
    } catch (error) {
      console.error('Failed to save workout to Firestore:', error);
      // Fall back to safe storage
    }
  }
  
  // Save to safe storage (for guests or as fallback)
  const updatedSavedWorkouts = [...savedWorkouts, workoutToSave];
  setToStorage('savedWorkouts', updatedSavedWorkouts);
  
  return updatedSavedWorkouts;
};

/**
 * Delete a workout
 * @param {number} workoutId - ID of workout to delete
 * @param {Array} savedWorkouts - Current saved workouts
 * @returns {Promise<Array>} Updated saved workouts array
 */
export const deleteWorkout = async (workoutId, savedWorkouts) => {
  const user = getCurrentUser();
  
  // If user is logged in, delete from Firestore
  if (user && user.uid) {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const existingWorkouts = userData.savedWorkouts || [];
        const updatedWorkouts = existingWorkouts.filter(w => w.id !== workoutId);
        
        await updateDoc(userDocRef, {
          savedWorkouts: updatedWorkouts
        });
        
        return updatedWorkouts;
      }
    } catch (error) {
      console.error('Failed to delete workout from Firestore:', error);
      // Fall back to safe storage
    }
  }
  
  // Delete from safe storage (for guests or as fallback)
  const updatedWorkouts = savedWorkouts.filter(w => w.id !== workoutId);
  setToStorage('savedWorkouts', updatedWorkouts);
  
  return updatedWorkouts;
};

/**
 * Helper: Get user tier from Firestore
 * @param {string} userId - Firebase user ID
 * @returns {Promise<string>} User tier
 */
const getUserTierFromFirestore = async (userId) => {
  try {
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return userDoc.data().tier || USER_TIERS.BASIC;
    }
    return USER_TIERS.BASIC;
  } catch (error) {
    console.error('Failed to get user tier from Firestore:', error);
    return USER_TIERS.BASIC;
  }
};